import React, { useState, useEffect } from "react";
import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";
import "./PluginCard.scss";

const PluginCard = ({
	slug,
	Name,
	Description,
	Image,
	ActiveInstallations,
	LastUpdated,
	Compatibility,
	Rating,
	NumberOfRatings,
	AuthorUrl,
	PluginUrl,
	DownloadLink,
}) => {
	// Possible statuses: 'not_installed', 'installed', 'activated', 'installing', 'activating', 'error'
	const [pluginStatus, setPluginStatus] = useState("checking");
	const [pluginFile, setPluginFile] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");

	// Check plugin status on component mount
	useEffect(() => {
		checkPluginStatus();
	}, [slug]);

	const checkPluginStatus = async () => {
		setPluginStatus("checking");
		setErrorMessage("");

		try {
			const response = await apiFetch({
				path: `/ultimate-security/v1/plugins/status`,
				method: "POST",
				data: {
					_wpnonce: window.ultimate_security_ajax_obj.security,
					slug: slug,
				},
			});

			if (response.status) {
				setPluginStatus(response.status);
				if (response.plugin_file) {
					setPluginFile(response.plugin_file);
				}
			} else {
				setPluginStatus("not_installed");
			}
		} catch (error) {
			console.error("Error checking plugin status:", error);
			setPluginStatus("not_installed");
		}
	};

	const renderStars = (rating) => {
		const fullStars = Math.floor(rating);
		const hasHalfStar = rating % 1 !== 0;
		const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
	
		return (
			<>
				{[...Array(fullStars)].map((_, i) => (
					<img src={ultimate_security_ajax_obj.imageUrl+"StarFilled.svg"} key={`full-${i}`} />
				))}
	
				{hasHalfStar && <img src={ultimate_security_ajax_obj.imageUrl+"StarHalf.svg"} key="half" />}
	
				{[...Array(emptyStars)].map((_, i) => (
					<img src={ultimate_security_ajax_obj.imageUrl+"StarEmpty.svg"} key={`empty-${i}`} />
				))}
			</>
		);
	};
	

	const installPlugin = async () => {
		setPluginStatus("installing");
		setErrorMessage("");
		try {
			// Configure apiFetch to catch and handle non-JSON responses
			apiFetch.use(
				apiFetch.createRootURLMiddleware(window.ultimate_security_ajax_obj.rest_url),
			);

			// Add custom middleware to handle potential non-JSON responses
			apiFetch.use(async (options, next) => {
				try {
					return await next(options);
				} catch (error) {
					// Check if this is a JSON parsing error
					if (
						error.message.includes("invalid json response") &&
						error.response
					) {
						try {
							// Try to extract JSON from potential HTML output
							const responseText = await error.response.text();
							const jsonMatch = responseText.match(/\{.*\}/s);
							if (jsonMatch) {
								return JSON.parse(jsonMatch[0]);
							}
						} catch (extractError) {
							console.error(
								"Failed to extract JSON from response:",
								extractError,
							);
						}
					}
					throw error;
				}
			});

			const installResponse = await apiFetch({
				path: "/ultimate-security/v1/plugins/install",
				method: "POST",
				data: {
					_wpnonce: window.ultimate_security_ajax_obj.security,
					plugin_name: Name,
					download_url: DownloadLink,
				},
			});

			if (installResponse.success && installResponse.plugin_file) {
				setPluginFile(installResponse.plugin_file);
				// First show "Installed" status
				setPluginStatus("installation_complete");

				// Then after a brief delay, change to "Activate" status
				setTimeout(() => {
					setPluginStatus("installed");
				}, 1500); // 1.5 seconds delay
			} else {
				throw new Error(
					installResponse.message || "Plugin installation failed",
				);
			}
		} catch (error) {
			console.error("Plugin Installation Error:", error);
			setErrorMessage(error.message || "Installation failed");
			setPluginStatus("error");
		}
	};

	const activatePlugin = async () => {
		if (!pluginFile) {
			setErrorMessage("Plugin file not found");
			setPluginStatus("error");
			return;
		}

		setPluginStatus("activating");
		setErrorMessage("");

		try {
			const activateResponse = await apiFetch({
				path: "/ultimate-security/v1/plugins/activate",
				method: "POST",
				data: {
					_wpnonce: window.ultimate_security_ajax_obj.security,
					plugin_file: pluginFile,
				},
			});

			if (activateResponse.success) {
				setPluginStatus("activated");
			} else {
				throw new Error(
					activateResponse.message || "Activation failed",
				);
			}
		} catch (error) {
			console.error("Plugin Activation Error:", error);
			setErrorMessage(error.message || "Activation failed");
			setPluginStatus("error");
		}
	};

	const getButtonLabel = () => {
		switch (pluginStatus) {
			case "checking":
				return __("Checking...", "ultimate-security");
			case "not_installed":
				return __("Install Now", "ultimate-security");
			case "installed":
				return __("Activate", "ultimate-security");
			case "installing":
				return __("Installing...", "ultimate-security");
			case "installation_complete": // New state
				return __("Installed", "ultimate-security");
			case "activating":
				return __("Activating...", "ultimate-security");
			case "activated":
				return __("Activated", "ultimate-security");
			case "error":
				return __("Try Again", "ultimate-security");
			default:
				return __("Install Now", "ultimate-security");
		}
	};

	const handleButtonClick = () => {
		switch (pluginStatus) {
			case "not_installed":
				installPlugin();
				break;
			case "installed":
				activatePlugin();
				break;
			case "error":
				checkPluginStatus();
				break;
			default:
				break;
		}
	};

	const isButtonDisabled = ["checking", "installing", "activating","installation_complete"].includes(
		pluginStatus,
	);

	return (
		<div className="plugin-card">
			<div className="top-section">
				<div className="pl-icon">
					<img
						src={Image}
						alt="Plugin Icon"
						width="128"
						height="128"
					/>
				</div>
				<div className="plugin-info">
					<div className="info-container">
						<h2 className="plugin-title">
							{__(Name, "ultimate-security")}
						</h2>
						<p className="plugin-description">
							{__(Description, "ultimate-security")}
						</p>
						<p className="plugin-author">
							{__("By", "ultimate-security")}{" "}
							<a href={AuthorUrl} className="author-name">
								{__("WPUltimateSecurity", "ultimate-security")}
							</a>
						</p>
						{errorMessage && (
							<p className="plugin-error-message">
								{errorMessage}
							</p>
						)}
					</div>
				</div>
				<div className="ac-buttons">
					<button
						onClick={handleButtonClick}
						className={`install-button ${pluginStatus}`}
						disabled={isButtonDisabled}
					>
						{getButtonLabel()}
					</button>
					<a
						href={PluginUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="more-details"
					>
						{__("More Details", "ultimate-security")}
					</a>
				</div>
			</div>

			<div className="bottom-section">
				<div className="rating-section">
					<div className="stars-container">
						<div className="stars">{renderStars(Rating)}</div>
						<span className="rating-count">
							({__(NumberOfRatings, "ultimate-security")})
						</span>
					</div>
					<p className="installation-count">
						{__(ActiveInstallations, "ultimate-security")}
					</p>
				</div>
				<div className="update-info">
					<div className="last-updated">
						<span className="update-label">
							{__("Last Updated", "ultimate-security")}:
						</span>
						<span className="update-time">
							{__(LastUpdated, "ultimate-security")}
						</span>
					</div>
					<p className="compatibility">
						{__(Compatibility, "ultimate-security")}
					</p>
				</div>
			</div>
		</div>
	);
};

export default PluginCard;
